import {
  axios,
  clear_entity_profile_list_cache,
  create_company_cases_state,
  get_entity_type_name,
  get_request_config,
  normalize_key,
  normalize_text,
  is_open_case_status,
  read_session_current_role,
  read_session_current_user_id,
  read_session_project_id,
  to_number,
} from './company_cases_helpers';
import { can_create_case, can_route_case } from './company_cases_permissions';
import { company_cases_loading_methods } from './company_cases_loading';
import { company_cases_creation_methods } from './company_cases_creation';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import {
  assign_requestor_application_users,
  fetch_requestor_application_forms,
  update_requestor_application_status,
} from '@/services/requestor_portal_service';
import { fetch_assign_form_status_data, fetch_user_roles } from '@/services/workflow_service';
import { GET_ROLE_GROUPS, GET_USERS } from '@/utils/system_constants/constant.js';

function case_detail_query(case_item = {}) {
  return Object.fromEntries([
    ['application_id', case_item?.application_id],
    ['entity_profile_id', case_item?.entity_profile_id],
    ['entity_type_id', case_item?.entity_type_id],
  ].filter(([, value]) => Boolean(value)));
}

function sla_state_text(case_item = {}) {
  return normalize_key([
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ].filter(Boolean).join(' '));
}

function has_sla_breach(case_item = {}) {
  const state = sla_state_text(case_item);
  return ['overdue', 'breach', 'breached', 'escalated', 'danger'].some((flag) => state.includes(flag));
}

function has_sla_warning(case_item = {}) {
  const state = sla_state_text(case_item);
  return ['warning', 'at-risk', 'text-warning', 'overdue', 'breach', 'breached', 'escalated', 'danger']
    .some((flag) => state.includes(normalize_key(flag)));
}

function parse_response_data(response) {
  const raw_data = response?.data ?? response ?? {};
  if (typeof raw_data !== 'string') return raw_data;

  try {
    const parsed = JSON.parse(raw_data);
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch {
    return {};
  }
}

function response_items(response, keys = []) {
  const parsed = parse_response_data(response);
  const nested = parse_response_data(parsed?.data);
  const candidates = [
    nested,
    parsed?.data,
    ...keys.map((key) => parsed?.[key]),
    parsed?.results,
    parsed,
  ];
  return candidates.find(Array.isArray) || [];
}

function uniq_by(items = [], key_builder = (item) => item) {
  const seen = new Set();
  return items.filter((item) => {
    const key = key_builder(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function workflow_status_option(status = {}) {
  const id = to_number(status?.id || status?.status_id);
  const label = normalize_text(status?.status || status?.status_name || status?.label || status?.name);
  const order = Number(status?.order_number ?? status?.order ?? status?.priority ?? 0);
  const is_final = Boolean(status?.is_final);
  return id && label ? { id, label, order, is_final } : null;
}

function normalized_route_role_label(value = '') {
  const label = normalize_text(value);
  return normalize_key(label) === 'system-owner' ? 'Coordinator' : label;
}

function role_option(role = {}) {
  const id = to_number(role?.id);
  const label = normalized_route_role_label(role?.role || role?.role_name || role?.name);
  const group_id = to_number(role?.group_id || role?.group?.id || role?.group);
  const group_name = normalize_text(role?.group_name || role?.group?.name);
  return id && label ? { id, label, group_id, group_name } : null;
}

function route_role_key(role = {}) {
  return normalize_key(role?.label);
}

function route_group_option(role = {}) {
  if (!role?.group_id || !role?.group_name) return null;
  return {
    id: role.group_id,
    label: role.group_name,
    role_id: role.id,
  };
}

function user_option(user = {}) {
  const user_id = to_number(user?.user_id || user?.id || user?.user?.id);
  const role = normalized_route_role_label(user?.user?.role?.role || user?.role_name || user?.role);
  const group_id = to_number(user?.user?.role?.group_id || user?.group_id);
  const group_name = normalize_text(user?.user?.role?.group_name || user?.group_name);
  const name = normalize_text([
    user?.user?.first_name,
    user?.user?.last_name,
  ].filter(Boolean).join(' ')) || normalize_text(
    user?.user?.email || user?.user?.username || user?.name
  );

  return user_id && name
    ? { user_id, name, role, group_id, group_name }
    : null;
}

function assignment_label(user = {}) {
  return [user.name, user.role].filter(Boolean).join(' - ');
}

function route_note_label(names = [], count = 0) {
  const selected_names = names.filter(Boolean).join(', ');
  if (selected_names) return `Sent to ${selected_names}.`;
  return count === 1 ? 'Sent to one team member.' : `Sent to ${count} team members.`;
}

function csv_value(value = '') {
  let normalized = String(value ?? '').replace(/\r?\n/g, ' ').trim();
  if (/^[=+\-@]/.test(normalized)) {
    normalized = `'${normalized}`;
  }
  return `"${normalized.replace(/"/g, '""')}"`;
}

function export_file_stamp() {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

function case_export_rows(cases = []) {
  return cases.map((case_item) => ({
    'Case code': case_item?.case_code,
    Title: case_item?.title,
    Description: case_item?.subtitle,
    Domain: case_item?.domain,
    Status: case_item?.status,
    Risk: [case_item?.risk_score, case_item?.risk_label].filter((value) => value !== undefined && value !== null && value !== '').join(' - '),
    Owner: case_item?.owner,
    SLA: case_item?.sla,
  }));
}

function cases_export_csv(cases = []) {
  const headers = [
    'Case code',
    'Title',
    'Description',
    'Domain',
    'Status',
    'Risk',
    'Owner',
    'SLA',
  ];
  const rows = case_export_rows(cases);
  const body = rows.map((row) => headers.map((header) => csv_value(row[header])).join(','));
  return `\uFEFFsep=,\r\n${[headers.map(csv_value).join(','), ...body].join('\r\n')}`;
}

function application_form_id(record = {}) {
  return to_number(record?.form_id || record?.form?.id || record?.id);
}

function unique_statuses(statuses = []) {
  return uniq_by(statuses, (status) => String(status?.id)).sort((left, right) => {
    const left_order = Number(left?.order || 0);
    const right_order = Number(right?.order || 0);
    if (left_order !== right_order) return left_order - right_order;
    return String(left?.label || '').localeCompare(String(right?.label || ''));
  });
}

function assigned_statuses_from_response(response) {
  return response_items(response, ['assigned_statuses', 'selected_statuses']);
}

function status_filter_option(status = {}) {
  if (!status?.label) return null;

  return {
    key: normalize_key(status.label),
    label: status.label,
    id: status.id,
    is_final: Boolean(status.is_final),
  };
}

function is_coordinator_intake_route(route_name = '') {
  return String(route_name || '').trim() === 'CoordinatorIntake';
}

function is_coordinator_escalations_route(route_name = '') {
  return String(route_name || '').trim() === 'CoordinatorEscalations';
}

export default {
  name: 'CompanyCases',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => create_company_cases_state(),
  computed: {
    project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    route_filter() {
      if (is_coordinator_escalations_route(this.$route?.name)) {
        return 'sla-breached';
      }

      return normalize_key(this.$route?.query?.filter);
    },
    route_status_filter() {
      return normalize_key(this.$route?.query?.status) || 'all';
    },
    should_open_new_case() {
      return is_coordinator_intake_route(this.$route?.name)
        || String(this.$route?.query?.open_new_case || '') === '1';
    },
    metrics() {
      const open_case_count = this.cases.filter((case_item) => is_open_case_status(case_item?.status)).length;
      const breached_count = this.cases.filter(has_sla_breach).length;
      const warning_count = this.cases.filter(has_sla_warning).length;

      return [
        { label: 'Open Cases', value: String(open_case_count), change: this.loading_cases ? 'Loading' : 'Live queue', change_class: 'secondary-color', icon: 'bi-folder2-open' },
        { label: 'SLA Warning', value: String(warning_count), change: '<= 48hr to breach', change_class: 'text-secondary', icon: 'bi-clock-history' },
        { label: 'SLA Breached', value: String(breached_count), change: breached_count ? 'Action required' : 'None', change_class: 'text-secondary', icon: 'bi-exclamation-triangle' },
        { label: 'Avg. Resolution', value: '-', change: 'Cases', change_class: 'secondary-color', icon: 'bi-arrow-down-left' }
      ];
    },
    display_filters() {
      const entity_type_filters = this.case_types
        .map((entity_type) => {
          const label = get_entity_type_name(entity_type);
          return label
            ? { key: normalize_key(label), label }
            : null;
        })
        .filter(Boolean);

      return [
        { key: 'all', label: 'All domains' },
        ...entity_type_filters,
        { key: 'high-risk', label: 'High Risk' },
        { key: 'sla-breached', label: 'SLA Breached' }
      ];
    },
    display_status_filters() {
      const workflow_filters = uniq_by(
        this.workflow_status_filter_options
          .map(status_filter_option)
          .filter(Boolean),
        (status) => status.key
      );

      return [
        { key: 'all', label: 'Filter by status' },
        ...workflow_filters,
      ];
    },
    active_status_filter() {
      return this.display_status_filters.find((item) => item.key === this.route_status_filter)?.key || 'all';
    },
    filtered_cases() {
      let visible_cases = this.cases;

      if (this.active_filter === 'high-risk') {
        visible_cases = this.cases.filter((item) => item.risk_label === 'High');
      } else if (this.active_filter === 'sla-breached') {
        visible_cases = this.cases.filter(has_sla_breach);
      } else if (this.active_filter !== 'all') {
        visible_cases = this.cases.filter((item) => item.domain_key === this.active_filter);
      }

      if (this.active_status_filter === 'all') {
        return visible_cases;
      }

      return visible_cases.filter((item) => normalize_key(item.status) === this.active_status_filter);
    },
    total_pages() {
      return Math.max(1, Math.ceil(this.filtered_cases.length / Number(this.page_size || 10)));
    },
    paginated_cases() {
      const page_size = Number(this.page_size || 10);
      const current_page = Math.min(this.current_page, this.total_pages);
      const start = (current_page - 1) * page_size;
      return this.filtered_cases.slice(start, start + page_size);
    },
    pagination_start() {
      if (!this.filtered_cases.length) return 0;
      return ((Math.min(this.current_page, this.total_pages) - 1) * Number(this.page_size || 10)) + 1;
    },
    pagination_end() {
      return Math.min(Math.min(this.current_page, this.total_pages) * Number(this.page_size || 10), this.filtered_cases.length);
    },
    pagination_summary() {
      if (!this.filtered_cases.length) return 'Showing 0 of 0 cases';
      return `Showing ${this.pagination_start.toLocaleString('en-ZA')} to ${this.pagination_end.toLocaleString('en-ZA')} of ${this.filtered_cases.length.toLocaleString('en-ZA')} cases`;
    },
    visible_page_numbers() {
      const pages = [];
      const start = Math.max(1, Math.min(this.current_page, this.total_pages) - 2);
      const end = Math.min(this.total_pages, start + 4);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
    can_go_previous() {
      return this.current_page > 1;
    },
    can_go_next() {
      return this.current_page < this.total_pages;
    },
    filter_notice() {
      const filter = this.display_filters.find((item) => item.key === this.active_filter);
      const status_filter = this.display_status_filters.find((item) => item.key === this.active_status_filter);
      const labels = [filter?.label, status_filter?.label]
        .filter(Boolean)
        .filter((label) => label !== 'All domains' && label !== 'Filter by status');

      if (!labels.length) {
        return '';
      }

      return `${labels.join(' / ')} - ${this.filtered_cases.length} shown`;
    },
    new_case_domain_options() {
      return this.case_types
        .map((entity_type) => ({
          id: to_number(entity_type?.id),
          label: get_entity_type_name(entity_type) || `Entity type ${entity_type?.id}`,
        }))
        .filter((entity_type) => entity_type.id);
    },
    can_create_new_case() {
      return can_create_case(this);
    },
    can_route_cases() {
      return can_route_case(this);
    },
    current_role() {
      return this.$store?.getters?.currentRole || read_session_current_role();
    },
    route_destination_options() {
      return uniq_by(this.route_role_options, route_role_key);
    },
    selected_route_role() {
      return this.route_destination_options.find((role) => String(role.id) === String(this.route_destination)) || null;
    },
    available_route_groups() {
      const selected_role = this.selected_route_role;
      if (!selected_role) return [];

      return uniq_by(
        this.route_role_options
          .filter((role) => route_role_key(role) === route_role_key(selected_role))
          .map(route_group_option)
          .filter(Boolean),
        (group) => String(group.id)
      );
    },
    selected_route_group() {
      return this.available_route_groups.find((group) => String(group.id) === String(this.route_group)) || null;
    },
    available_route_users() {
      const selected_role = this.selected_route_role;
      const selected_group = this.selected_route_group;
      if (!selected_role || !selected_group) return [];

      const is_coordinator_destination = route_role_key(selected_role) === 'coordinator';
      const current_user_id = read_session_current_user_id();

      return this.route_user_options.filter((user) =>
        normalize_key(user.role) === route_role_key(selected_role)
        && Number(user.group_id) === Number(selected_group.id)
        && (!is_coordinator_destination || Number(user.user_id) !== Number(current_user_id))
      );
    },
    can_save_route_case() {
      return Boolean(
        this.route_case
        && this.selected_route_status
        && this.selected_route_role
        && this.selected_route_group
        && this.route_selected_user_ids.length
        && !this.submitting_route_case
      );
    },
    selected_route_status() {
      return this.route_status_options.find((status) => String(status.id) === String(this.route_priority)) || null;
    },
    onboarding_preview_forms() {
      return Array.isArray(this.onboarding_preview?.forms)
        ? this.onboarding_preview.forms
        : [];
    },
    onboarding_completion() {
      return this.onboarding_status?.completion || {};
    },
    is_email_source() {
      return String(this.new_case_form.source_channel || '').toLowerCase() === 'email';
    },
    route_case_title() {
      const case_code = this.route_case?.case_code || 'Case';
      return `Triage & route - ${case_code}`;
    },
    route_case_subtitle() {
      return [
        this.route_case?.status || 'New case',
        this.route_case?.domain || 'Service',
      ].filter(Boolean).join(' - ');
    },
    page_loading() {
      return this.loading_cases;
    },
    page_loading_message() {
      return 'Loading cases...';
    },
    page_loading_detail() {
      return 'Fetching the latest case queue from the backend.';
    },
    modal_loading() {
      return this.show_new_case_modal && (this.onboarding_preview_loading || this.submitting_new_case);
    },
    modal_loading_message() {
      if (this.submitting_new_case) {
        return 'Creating case and running onboarding...';
      }

      return 'Preparing onboarding preview...';
    },
    modal_loading_detail() {
      if (this.submitting_new_case) {
        return 'Please wait while we create the case and start the workflow.';
      }

      return 'Loading the requestor experience for the selected domain.';
    }
  },
  watch: {
    route_filter: {
      immediate: true,
      handler(filter_key) {
        const next_filter = this.display_filters.find((item) => item.key === filter_key)?.key || 'all';
        this.active_filter = next_filter;
        this.reset_pagination();
      }
    },
    route_status_filter() {
      this.reset_pagination();
    },
    filtered_cases() {
      if (this.current_page > this.total_pages) {
        this.current_page = this.total_pages;
      }
    },
    should_open_new_case: {
      immediate: true,
      handler(should_open) {
        if (!should_open || this.show_new_case_modal || !this.case_types.length) {
          return;
        }

        this.open_new_case_modal();
      }
    },
    case_types: {
      deep: true,
      handler() {
        if (this.should_open_new_case && !this.show_new_case_modal && this.case_types.length) {
          this.open_new_case_modal();
        }
      }
    }
  },
  created() {
    this.load_company_cases();
  },
  methods: {
    ...company_cases_loading_methods,
    ...company_cases_creation_methods,
    assignment_label,
    async load_workflow_status_filter_options() {
      this.workflow_status_filter_options = uniq_by(
        this.cases
          .map((case_item) => {
            const label = normalize_text(case_item?.status);
            return label ? { id: label, label, order: 0, is_final: !is_open_case_status(label) } : null;
          })
          .filter(Boolean),
        (status) => normalize_key(status.label)
      );
    },
    async load_route_modal_options() {
      this.route_loading = true;
      this.route_error = '';

      try {
        const application_forms = this.route_case?.application_id
          ? await fetch_requestor_application_forms(this.route_case.application_id)
          : [];
        const form_ids = [...new Set(application_forms.map(application_form_id).filter(Boolean))];
        const status_groups = await Promise.all(
          form_ids.map((form_id) => fetch_assign_form_status_data({ form_id }))
        );

        const [roles, role_groups_response, users_response] = await Promise.all([
          fetch_user_roles(this.project_id ? { project_id: this.project_id } : {}),
          axios.get(GET_ROLE_GROUPS, get_request_config()),
          axios.get(GET_USERS, get_request_config({
            params: {
              page: 1,
              page_size: 500,
              ...(this.project_id ? { project_id: this.project_id } : {}),
            },
          })),
        ]);

        this.route_status_options = unique_statuses(
          status_groups
            .flatMap((response) => assigned_statuses_from_response(response))
            .map(workflow_status_option)
            .filter(Boolean)
        );

        const role_groups = response_items(role_groups_response, ['role_groups'])
          .map((group) => ({
            id: to_number(group?.id || group?.group_id),
            label: normalize_text(group?.name || group?.group_name),
          }))
          .filter((group) => group.id && group.label);

        this.route_role_options = response_items(roles, ['roles'])
          .map(role_option)
          .filter(Boolean)
          .filter((role) => role.group_id)
          .map((role) => {
            const matched_group = role_groups.find((group) => group.id === role.group_id);
            return matched_group
              ? { ...role, group_name: matched_group.label }
              : role;
          });

        this.route_user_options = response_items(users_response, ['users'])
          .map(user_option)
          .filter(Boolean);

        if (!this.selected_route_status && this.route_status_options[0]) {
          this.route_priority = String(this.route_status_options[0].id);
        }

        if (!this.selected_route_role && this.route_destination_options[0]) {
          this.route_destination = String(this.route_destination_options[0].id);
        }

        if (!this.selected_route_group && this.available_route_groups[0]) {
          this.route_group = String(this.available_route_groups[0].id);
        }

        if (!this.route_status_options.length) {
          this.route_error = 'No workflow statuses are configured for this case.';
        }
      } catch (error) {
        this.route_error = error?.response?.data?.message || error?.message || 'Failed to load routing options.';
      } finally {
        this.route_loading = false;
      }
    },
    set_filter(filter_key) {
      this.active_filter = filter_key;
      const route_name = filter_key === 'sla-breached' ? 'CoordinatorEscalations' : 'CompanyCases';
      this.$router.replace({
        name: route_name,
        params: this.$route.params,
        query: {
          ...this.$route.query,
          ...(filter_key === 'all' ? { filter: undefined } : { filter: filter_key }),
        },
      });
    },
    set_status_filter(status_key) {
      const next_status = normalize_key(status_key) || 'all';
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          ...this.$route.query,
          ...(next_status === 'all' ? { status: undefined } : { status: next_status }),
        },
      });
    },
    reset_pagination() {
      this.current_page = 1;
    },
    set_page(page) {
      const normalized_page = Number(page);
      if (!Number.isFinite(normalized_page)) return;
      this.current_page = Math.min(Math.max(1, normalized_page), this.total_pages);
    },
    previous_page() {
      if (this.can_go_previous) {
        this.set_page(this.current_page - 1);
      }
    },
    next_page() {
      if (this.can_go_next) {
        this.set_page(this.current_page + 1);
      }
    },
    export_cases_csv() {
      if (!this.filtered_cases.length) {
        this.cases_error = 'There are no cases to export for the selected filters.';
        return;
      }

      this.cases_error = '';
      this.download_text_file(
        `case-queue-${export_file_stamp()}.csv`,
        cases_export_csv(this.filtered_cases),
        'text/csv;charset=utf-8;'
      );
    },
    download_text_file(filename, content, type) {
      const blob = new Blob([content], { type });
      const download_url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = download_url;
      link.setAttribute('download', filename);
      link.setAttribute('rel', 'noopener');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(download_url), 0);
    },
    go_to_case_detail(case_item) {
      const case_code = typeof case_item === 'string' ? case_item : case_item?.case_code;
      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_code },
        query: case_detail_query(case_item),
      });
    },
    async open_route_case_modal(case_item) {
      if (!this.can_route_cases) return;
      this.route_case = case_item;
      this.route_priority = '';
      this.route_destination = '';
      this.route_group = '';
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
      this.route_error = '';
      this.show_route_case_modal = true;
      await this.load_route_modal_options();
    },
    close_route_case_modal() {
      this.show_route_case_modal = false;
      this.route_case = null;
      this.route_priority = '';
      this.route_destination = '';
      this.route_group = '';
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
      this.route_error = '';
    },
    sync_route_group_selection() {
      if (!this.selected_route_group && this.available_route_groups[0]) {
        this.route_group = String(this.available_route_groups[0].id);
      }
      if (!this.available_route_groups.length) {
        this.route_group = '';
      }
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
    },
    sync_route_user_selection() {
      const available_ids = new Set(this.available_route_users.map((user) => String(user.user_id)));
      this.route_selected_user_ids = this.route_selected_user_ids.filter((user_id) => available_ids.has(String(user_id)));
      this.route_select_all_users = this.available_route_users.length > 0
        && this.route_selected_user_ids.length === this.available_route_users.length;
    },
    toggle_all_route_users() {
      this.route_selected_user_ids = this.route_select_all_users
        ? this.available_route_users.map((user) => String(user.user_id))
        : [];
    },
    async route_selected_case() {
      if (!this.can_save_route_case || !this.route_case?.application_id) {
        this.route_error = 'Select a workflow status, role group, and at least one user before routing.';
        return;
      }

      this.submitting_route_case = true;
      this.route_error = '';

      try {
        const application_id = Number(this.route_case.application_id);
        const entity_profile_id = Number(this.route_case.entity_profile_id) || null;
        const role_id = Number(this.selected_route_group?.role_id || this.selected_route_role?.id);
        const selected_user_ids = this.route_selected_user_ids.map((user_id) => Number(user_id)).filter(Boolean);
        const selected_status = this.selected_route_status;
        const selected_user_names = selected_user_ids
          .map((user_id) => this.available_route_users.find((user) => Number(user.user_id) === user_id)?.name)
          .filter(Boolean);

        await assign_requestor_application_users({
          application_id,
          assignments: selected_user_ids.map((user_id) => ({
            user_id,
            role_id,
          })),
        });

        if (selected_status?.label) {
          await update_requestor_application_status({
            application_id,
            entity_profile_id,
            status: selected_status.label,
            note: route_note_label(selected_user_names, selected_user_ids.length),
            action_taken: 'Case routed',
          });
        }

        clear_entity_profile_list_cache();
        await this.load_company_cases();
        this.close_route_case_modal();
      } catch (error) {
        this.route_error = error?.response?.data?.message || error?.message || 'Failed to route case.';
      } finally {
        this.submitting_route_case = false;
      }
    },
  }
};
